import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowRight, Sparkles } from 'lucide-react';
import type { SoftwareProfile } from '@/types/analysis';

interface ProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: Omit<SoftwareProfile, 'id' | 'createdAt'>) => void;
  editingProfile?: SoftwareProfile | null;
}

export function ProfileDialog({ open, onOpenChange, onSubmit, editingProfile }: ProfileDialogProps) {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    targetAudience: '',
    keyFeatures: '',
    website: '',
  });

  // Reset form when dialog opens/closes or editing profile changes
  useEffect(() => {
    if (editingProfile) {
      setFormData({
        name: editingProfile.name,
        category: editingProfile.category,
        description: editingProfile.description,
        targetAudience: editingProfile.targetAudience,
        keyFeatures: editingProfile.keyFeatures,
        website: editingProfile.website || '',
      });
    } else if (!open) {
      setFormData({
        name: '',
        category: '',
        description: '',
        targetAudience: '',
        keyFeatures: '',
        website: '',
      });
    }
  }, [editingProfile, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <div className="p-2 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 shadow-lg shadow-violet-500/25">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            {editingProfile ? 'Edit Profile' : 'Create Software Profile'}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {editingProfile
              ? 'Update your software information for better recommendations'
              : 'Tell us about your software to get personalized marketing insights'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-medium">
                Software Name *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., TaskMaster Pro"
                required
                className="rounded-xl h-10 text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category" className="text-xs font-medium">
                Category *
              </Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., SaaS, Developer Tools"
                required
                className="rounded-xl h-10 text-sm"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-xs font-medium">
              Description *
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what your software does and what problems it solves..."
              rows={3}
              required
              className="rounded-xl text-sm resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="targetAudience" className="text-xs font-medium">
              Target Audience *
            </Label>
            <Input
              id="targetAudience"
              value={formData.targetAudience}
              onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
              placeholder="e.g., Small business owners, Developers"
              required
              className="rounded-xl h-10 text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="keyFeatures" className="text-xs font-medium">
              Key Features *
            </Label>
            <Textarea
              id="keyFeatures"
              value={formData.keyFeatures}
              onChange={(e) => setFormData({ ...formData, keyFeatures: e.target.value })}
              placeholder="List your main features and what makes your software unique..."
              rows={3}
              required
              className="rounded-xl text-sm resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="website" className="text-xs font-medium">
              Website (optional)
            </Label>
            <Input
              id="website"
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://yourwebsite.com"
              className="rounded-xl h-10 text-sm"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 rounded-xl h-11"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white shadow-lg shadow-violet-500/25 rounded-xl h-11 text-sm font-medium"
            >
              {editingProfile ? 'Update Profile' : 'Create Profile'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
